import { showCardComponent } from './adyenCardPayments';
import { showOnlineBankingComponent } from './adyenOnlinePayments';
import {
    getPaymentWindowData,
    getWindowConfig,
    openPaymentWindow,
    PaymentWindowData,
    showWindow,
    showPayInfoOnBox, getPaymentResultUrls, showPaymentLoader, hidePaymentLoader, getInitiatedTransactionId
} from '../paymentsHelper';
import UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import initAdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import { Localization } from '../../../localization/localization';
import { CheckoutSession } from '@adyen/adyen-web/dist/types/types';
import { Locker } from '../../../utils/locker';
import { isValidEmail } from '../../modules/validators';
import { WindowData } from '../types/windowData';
import { AdyenConfig } from '../types/adyenConfig';
import { AdyenResult, AdyenState } from '../types/adyenState';
import { AdyenCreatePaymentResponse } from '../types/adyenCreatePaymentResponse';
import { showDropInComponent } from './adyenDropInPayments';
import { PayServiceApi } from '../../../api/PayServiceApi';
import { PaymentServices } from '../../../payments/paymentServices';
import { Url } from '../../modules/url';
import { AdyenCreateSessionRequest } from '../types/adyenCreateSessionRequest';
import { AdyenCreatePaymentRequest } from '../types/adyenCreatePaymentRequest';
import { AdyenCreatePaymentLinkRequest } from '../types/adyenCreatePaymentLinkRequest';
import { CurrentUser } from '../../modules/user/currentUser';

const cardMethods: string[] = [
    'bcmc',
    'cartebancaire',
    'visa,mc,amex',
    'amex',
    'diners',
    'discover',
    'kcp_creditcard',
    'maestro'
];

const onlineBankingMethods: string[] = [
    'giropay',
    'directEbanking',
    'ideal',
    'dotpay',
    'ebanking_FI'
];

const locker = Locker.getInstance();
const lockerKey: string = 'makePayment';
let shopperEmail = '';
let transactionId: number;

export async function showAdyenDropInComponent() {
    await createInitialTransaction();
    if (!transactionId) {
        return;
    }

    clearFooterText();
    hidePaymentVariantsHeader();
    deleteOldForms();
    showPaymentLoader();
    await showDropInComponent();
    setFooterText();
    hidePaymentLoader();
}

export async function makeAdyenPayment(type: string) {
    hidePaymentVariants();
    deleteOldForms();


    if (window.app.config.payments.adyen.web_component_integration === false) {
        openPaymentWindow(type, false, window.app.config.payments.adyen.paymentWindowUrl);
    } else {
        await createInitialTransaction();
        if (!transactionId) {
            return;
        }

        if (cardMethods.includes(type)) {
            await showCardComponent(type);
        } else if (onlineBankingMethods.includes(type)) {
            await showOnlineBankingComponent(type);
        } else {
            const createPaymentLinkRequest: AdyenCreatePaymentLinkRequest = {
                country: window.app.config.country,
                locale: window.app.config.locale,
                method: type,
                transactionId
            };
            const redirectUrl =
                await PayServiceApi.post<string>('payments/adyen/paymentLink', createPaymentLinkRequest);
            const windowData: WindowData = {
                windowFeatures: getWindowParams(),
                windowName: 'adyen',
                windowRequestUrl: redirectUrl
            };
            showWindow(windowData);
        }
    }
}

export function hidePaymentVariants() {
    const popupInfo: HTMLElement = document.querySelector('#ecommpay_order_box_popup_payment_variants');
    popupInfo.style.display = 'none';
}

function hidePaymentVariantsHeader() {
    const paymentVariantsHeader: HTMLElement =
        document.querySelector('#ecommpay_order_box_popup_payment_variants .ecommpay_order_box_header');
    paymentVariantsHeader.style.display = 'none';
}

function getPaymentsFooter(): HTMLElement {
    return document.querySelector('#ecommpay_order_box_popup_payment_variants .ecommpay_order_box_footer_text');
}

function clearFooterText() {
    const paymentVariantsFooter: HTMLElement = getPaymentsFooter();
    paymentVariantsFooter.innerHTML = '';
}

function setFooterText() {
    const paymentVariantsFooter: HTMLElement = getPaymentsFooter();
    const termsOfServiceLink = Url.getFullLangUrl('/#terms_of_service');
    const privacyPolicyLink = Url.getFullLangUrl('/#privacy_policy');
    const eulaPolicyLink = Url.getFullLangUrl('/#eula_policy');
    let agreementText = Localization._n('payments.agreement_text');
    const linkOpt = `target='_blank'`;
    agreementText = Localization.addLinkToText(agreementText, termsOfServiceLink, linkOpt, '%tos%');
    agreementText = Localization.addLinkToText(agreementText, privacyPolicyLink, linkOpt, '%privacy%');
    agreementText = Localization.addLinkToText(agreementText, eulaPolicyLink, linkOpt, '%eula%');
    paymentVariantsFooter.innerHTML = agreementText;
}

// eslint-disable-next-line max-lines-per-function,max-len
export async function getAdyenCommonConfig(type: string, container: HTMLElement = null, withSession: boolean = false): Promise<AdyenConfig> {
    const paymentResultUrls = getPaymentResultUrls(type);
    const paymentData: PaymentWindowData = getPaymentWindowData(type);
    let environment: 'test' | 'live' | 'live-us' | 'live-au' | string;
    const environmentContinents = window.app.config.payments.adyen.environment_continents;
    for (const [env, continents] of Object.entries(environmentContinents)) {
        if (continents.includes(window.app.config.continent) || env === 'test') {
            environment = env;
            break;
        }
    }

    if (typeof environment === 'undefined') {
        showPayInfoOnBox(paymentResultUrls.error);
        throw new Error(`ADYEN_PAYMENT: could not find environment for continent ${window.app.config.continent}`);
    }

    const config: AdyenConfig = {
        clientKey: window.app.config.payments.adyen.clientKey,
        environment,
        locale: paymentData.locale,
        onPaymentCompleted: (result: AdyenResult, _component: UIElement) => {
            if (result.resultCode === 'Authorised') {
                showPayInfoOnBox(paymentResultUrls.success);
            } else {
                showPayInfoOnBox(paymentResultUrls.error);
            }
        },
        onSubmit: async(state: AdyenState, component: UIElement) => {
            if (state.isValid !== true) {
                showPayInfoOnBox(paymentResultUrls.error);
                return;
            }
            if (container !== null) {
                container.remove();
                showPaymentLoader();
            }
            try {
                const currentUser = CurrentUser.getInstance();
                const createPaymentRequest: AdyenCreatePaymentRequest = {
                    ...state.data,
                    country: window.app.config.country,
                    ip: window.app.config.ip,
                    locale: window.app.config.locale,
                    email: currentUser.getPaymentEmail(),
                    transactionId
                };
                if (shopperEmail !== '') {
                    createPaymentRequest.email = shopperEmail;
                }
                const createPaymentResponse = await PayServiceApi.post<AdyenCreatePaymentResponse>(
                    'payments/adyen/createPayment',
                    createPaymentRequest
                );
                console.debug(createPaymentResponse);
                hidePaymentLoader();
                if (createPaymentResponse.resultCode === 'Authorised') {
                    showPayInfoOnBox(paymentResultUrls.success);
                } else if ('action' in createPaymentResponse) {
                    if (createPaymentResponse.action.type === 'redirect') {
                        if (createPaymentResponse.action.method === 'GET') {
                            handleGetRedirect(createPaymentResponse.action.url);
                        } else {
                            const checkout = await initAdyenCheckout(config);
                            const checkoutAction: UIElement = checkout.createFromAction(createPaymentResponse.action);
                            handlePostRedirect(checkoutAction);
                        }
                    } else {
                        component.handleAction(createPaymentResponse.action);
                    }
                } else {
                    const resultWithErrorInfo = getPaymentResultUrls(type, createPaymentResponse.errorInfo);

                    showPayInfoOnBox(resultWithErrorInfo.error);
                }
            } catch (error) {
                showPayInfoOnBox(paymentResultUrls.error);
                window.Sentry.captureException(error);
            } finally {
                locker.releaseLock(lockerKey);
            }
        },
        showPayButton: false,
        translations: {
            [paymentData.locale]: {
                'creditCard.cvcField.placeholder': '',
                'creditCard.cvcField.placeholder.3digits': '',
                'creditCard.cvcField.placeholder.4digits': '',
                'creditCard.expiryDateField.placeholder': '',
                'creditCard.expiryDateField.title': Localization._n('stripe.card_date'),
                'creditCard.numberField.placeholder': ''
            }
        }
    };
    if (withSession === true) {
        config.session = await createSession();
    }
    return config;
}

export function setComponentHandlers(htmlElement: HTMLElement, adyenElement: UIElement, autoSubmit: boolean = false) {
    const backBtn = htmlElement.querySelector('.back_btn');
    backBtn.addEventListener('click', function backBtn() {
        window.app.order_box.showPaymentVariants();
        htmlElement.remove();
    });
    const submitBtn = htmlElement.querySelector('#submit');
    if (autoSubmit === true) {
        adyenElement.submit();
    } else {
        submitBtn.addEventListener('click', (event) => {
            event.preventDefault();
            if (validateEmail(htmlElement) === false) {
                showEmailError(htmlElement);
                return;
            }
            if (!locker.hasLock(lockerKey)) {
                adyenElement.submit();
                if (adyenElement.isValid === true) {
                    locker.setLock(lockerKey);
                }
            }
        });
    }
}

export function createSession(): Promise<CheckoutSession> {
    const createSessionRequest: AdyenCreateSessionRequest = {
        country: window.app.config.country,
        ip: window.app.config.ip,
        locale: window.app.config.locale,
        transactionId
    };
    return PayServiceApi.post<CheckoutSession>('payments/adyen/createSession', createSessionRequest);
}

function handleGetRedirect(url: string) {
    const windowData: WindowData = {
        windowFeatures: getWindowParams(),
        windowName: 'adyen',
        windowRequestUrl: url
    };
    showWindow(windowData);
}

function handlePostRedirect(checkoutAction: UIElement) {
    const windowData: WindowData = {
        windowFeatures: getWindowParams(),
        windowName: 'adyen',
        windowRequestUrl: ''
    };
    const paymentWindow: Window = showWindow(windowData);
    const container = document.createElement('div');
    container.setAttribute('id', 'actions-container');
    paymentWindow.document.body.append(container);
    checkoutAction.mount(container);
}

function getWindowParams(): string[] {
    const windowFeatures = getWindowConfig();
    const windowFeaturesArray = [];

    let param: keyof typeof windowFeatures;
    for (param in windowFeatures) {
        windowFeaturesArray.push(`${param}=${windowFeatures[param]}`);
    }
    return windowFeaturesArray;
}

function validateEmail(container: HTMLElement): boolean {
    shopperEmail = '';
    const email: HTMLInputElement = container.querySelector('input.email');
    if (email) {
        if (!isValidEmail(email.value)) {
            return false;
        }
        shopperEmail = email.value;
    }
    return true;
}

function showEmailError(container: HTMLElement) {
    const errors: HTMLDivElement = container.querySelector('.mail_block .errors');
    if (errors) {
        errors.textContent = Localization._n('errors.email_wrong');
    }
}

function deleteOldForms() {
    const adyenForms = document.querySelector('.ecommpay_variant.adyen, .ecommpay_variant.adyen-dropIn');
    if (adyenForms) {
        adyenForms.remove();
    }
}

async function createInitialTransaction() {
    transactionId = (await getInitiatedTransactionId(
        window.app.last_payment_object,
        PaymentServices.ADYEN
    ));
}
